import { useLocation, useNavigate, useSubmission } from '@solidjs/router';
import {
	createContext,
	createEffect,
	createMemo,
	createSignal,
	createUniqueId,
	splitProps,
	useContext,
} from 'solid-js';
import { cn } from 'tailwind-variants';
import type { Action } from '@solidjs/router';
import type { Accessor, JSX } from 'solid-js';

type Props = Omit<JSX.FormHTMLAttributes<HTMLFormElement>, 'onSubmit' | 'action'> & {
	id?: string;
	// oxlint-disable-next-line typescript/no-explicit-any
	action: Action<Array<any>, any>;
	ref?: (el: HTMLFormElement) => void;
};

type CTX = {
	data: Accessor<ReturnType<typeof useSubmission>>;
	errors: Accessor<Array<string>>;
	fieldErrors: Accessor<Record<string, Array<string>>>;
	hasChanges: Accessor<boolean>;
};

export const FormContext = createContext<CTX>({
	data: () => ({ pending: false, url: '', clear: () => {}, retry: () => {}, input: [], error: null, result: null }),
	errors: () => [],
	fieldErrors: () => ({}),
	hasChanges: () => false,
});

export function useFormContext() {
	return useContext(FormContext);
}

export function Form(formProps: Props) {
	const [formRef, setFormRef] = createSignal<HTMLFormElement>();
	const formId = createUniqueId();
	const [props, attrs] = splitProps(formProps, ['class', 'children']);
	const [fieldErrors, setFieldErrors] = createSignal<Record<string, Array<string>>>({});
	const [errors, setErrors] = createSignal<Array<string>>([]);
	const navigate = useNavigate();
	const location = useLocation();
	const [hasChanges, setHasChanges] = createSignal(false);

	const data = createMemo(() =>
		useSubmission(attrs.action, (args) => {
			const data = args[args.length - 1];
			return data instanceof FormData || data instanceof URLSearchParams
				? data.get('__formId') === (formProps.id ?? formId)
				: true;
		}),
	);

	createEffect(() => {
		if (data().pending && data().error) {
			data().clear();
		}
		if (data().pending || data().result) {
			setHasChanges(false);
		}
	});

	createEffect(() => {
		const errorResponse = data().error;
		if (!errorResponse) {
			setFieldErrors({});
			setErrors([]);
			return;
		}

		if (errorResponse.statusCode === 401 && !location.pathname.startsWith('/auth')) {
			const params = new URLSearchParams({ redirect: location.pathname });
			navigate(`/auth?${params.toString()}`, { state: { errorMessage: errorResponse.message } });
			return;
		}

		// TODO: if errorResponse is a TypeError, "Failed to fetch", have a way to bubble that up

		const error = errorResponse.error as
			| undefined
			| { code: number; message: string; fields: Record<string, Array<string>> };
		if (error?.message) {
			setErrors([error.message]);
		}

		setFieldErrors(error?.fields ?? {});
		(formRef()?.querySelector('[aria-invalid]') as HTMLInputElement)?.focus();
	});

	createEffect(() => {
		formRef()
			?.querySelectorAll('input,select,textarea')
			.forEach((el) => {
				(el as HTMLInputElement).addEventListener('input', () => {
					setHasChanges(true);
				});
			});
	});

	return (
		// @ts-expect-error for some reason action() isn't reporting as Serializable
		<form
			method="post"
			{...attrs}
			class={cn('flex w-full flex-col gap-6', props.class)}
			ref={(el) => {
				setFormRef(el);
				formProps.ref?.(el);
			}}
			{...disableAutocomplete(attrs)}
		>
			<input type="hidden" name="__formId" value={formProps.id ?? formId} />
			<FormContext.Provider value={{ data, errors, fieldErrors, hasChanges }}>{props.children}</FormContext.Provider>
		</form>
	);
}

export function disableAutocomplete(opts: {
	autocomplete?: JSX.InputHTMLAttributes<HTMLInputElement>['autocomplete'] | undefined;
}) {
	return {
		autocomplete: opts.autocomplete,
		'data-1p-ignore': opts.autocomplete === 'off' ? true : undefined,
		'data-lpignore': opts.autocomplete === 'off' ? 'true' : undefined,
		'data-form-type': opts.autocomplete === 'off' ? 'other' : undefined,
		'data-bwignore': opts.autocomplete === 'off' ? true : undefined,
	} as const;
}
